import { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { FilteredRolodexView } from './FilteredRolodexView';
import { motion, animate, useMotionValue, useDragControls } from 'framer-motion';
import CreateCommunityPost from "./CreateCommunityPost";
import { safeFetch, navigate } from "./App";
import CreateGroupModal from "./CreateGroupModal";
import RegisterBusinessForm from "./RegisterBusinessForm";
import { cacheSafeFetch, memoryCache } from "./cacheSafeFetch";
import ModerationReportButton from "./ModerationReportButton";
import { ZoneMapHero } from './components/zone/ZoneMapHero';
import { ZoneSectionNav } from './components/zone/ZoneSectionNav';
import { SkillsKnowledgeSection } from './components/zone/SkillsKnowledgeSection';
import { AddSkillModal } from './components/zone/AddSkillModal';
import { ZoneGroupChat } from './components/zone/ZoneGroupChat';
import { Building2, CalendarDays, Users, MessageSquare, List, Grid3x3, Map, Filter, Handshake, UserCircle, MessageCircle, Store, Edit, Trash2, Phone, Mail, Share2, Copy, Check, X, Lightbulb, Plus, Search, ArrowLeft, ShoppingBag } from 'lucide-react';
// "What's Happening Today" popup removed for now — keep imports handy for re-enabling.
// import WhatsHappeningTodayPopup from "./components/WhatsHappeningTodayPopup";
// import { fetchWhatsHappeningToday, shouldShowWhatsHappeningPopup, markWhatsHappeningPopupShown } from "./utils/whatsHappeningToday";
import { ShareMenu } from './components/ui/ShareMenu';
import {
    MemberDetailModal,
    ShareProfileModal,
    EditProfileModal,
    ZoneMembersModal,
    AboutTab,
    EntityListTab,
    StoresTab,
} from './components/zone/details';

// Mirrors ZoneMapHero's coordinate parsing so we can tell, in the parent,
// whether any entity will actually land on the map.
const parseEntityLatLng = (raw) => {
    if (!raw) return null;
    try {
        const loc = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (loc?.lat && loc?.lng) return [loc.lat, loc.lng];
        if (loc?.latitude && loc?.longitude) return [loc.latitude, loc.longitude];
    } catch { /* noop */ }
    return null;
};

// Helper function to convert URLs in text to clickable links
const renderTextWithLinks = (text) => {
    if (!text) return null;

    // Regular expression to match URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
        if (part.match(urlRegex)) {
            return (
                <a
                    key={index}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                    style={{ color: 'var(--color-brand-primary)' }}
                >
                    {part}
                </a>
            );
        }
        return part;
    });
};

export const ZoneDetails = ({ activeTab: externalActiveTab, onTabChange: externalOnTabChange }) => {
    const urlParams = new URLSearchParams(window.location.search);
    const chatId = localStorage.getItem('chat_id'); //urlParams.get('chat_id');
    const zone = urlParams.get('zone');
    const hasSetInitialTab = useRef(false);
    const hasSetInitialDetent = useRef(false);

    const [businesses, setBusinesses] = useState(null);
    const [events, setEvents] = useState(null);
    const [barters, setBarters] = useState(null);
    const [zoneCenter, setZoneCenter] = useState(null); // [lat, lng]
    const [otherZones, setOtherZones] = useState([]); // [{ name, id, lat, lng }] — every other zone, shown on the map
    const [categories, setCategories] = useState([]);
    const [eventsCategories, setEventsCategories] = useState([]);
    const [bartersCategories, setBartersCategories] = useState([]);
    const [groupsCategories, setGroupsCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [internalActiveTab, setInternalActiveTab] = useState('businesses');
    const activeTab = externalActiveTab || internalActiveTab;
    const setActiveTab = externalOnTabChange || setInternalActiveTab;
    const [categoryGroups, setCategoryGroups] = useState({});
    const [showCategoryGroups, setShowCategoryGroups] = useState(false);
    const [communityPosts, setCommunityPosts] = useState([]);
    const [postTypeFilter, setPostTypeFilter] = useState('all');
    const [openEmojiPanel, setOpenEmojiPanel] = useState(null); // track open post ID
    const emojiSet = ['👍', '👎', '❤️', '😂', '😮', '😢', '👏', '🔥', '😡', '🎉', '🙏']
    const [openCommentPanel, setOpenCommentPanel] = useState(null);
    const [commentText, setCommentText] = useState('');
    const [openCommentsPostId, setOpenCommentsPostId] = useState(null);
    const [postComments, setPostComments] = useState({});
    const [showPostOptions, setShowPostOptions] = useState(false);
    const [showCreatePostModal, setShowCreatePostModal] = useState(false);
    const [createPostType, setCreatePostType] = useState(null);
    const [groups, setGroups] = useState([]);
    const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
    const [showCreateEventModal, setShowCreateEventModal] = useState(false);
    const [showCreateBusinessModal, setShowCreateBusinessModal] = useState(false);
    const [showCreateBarterModal, setShowCreateBarterModal] = useState(false);
    const [allCategories, setAllCategories] = useState([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [viewMode, setViewMode] = useState('rolodex');
    const [showFilters, setShowFilters] = useState(false);
    const [activeFilterCount, setActiveFilterCount] = useState(0);
    const [communityMembers, setCommunityMembers] = useState([]);
    const [collapsedInfoBoxes, setCollapsedInfoBoxes] = useState({
        businesses: true,
        events: true,
        barters: true,
        groups: true,
        members: true,
        skills: true,
        stores: true
    });
    const [currentUserMember, setCurrentUserMember] = useState(null);

    const [zoneName, setZoneName] = useState(null);

    // Full-page map layout. Content lives in a single draggable sheet over the
    // map with three detents — 'peek' (just the section rail, the resting
    // state), 'half', and 'full'. The map stays alive behind it at every
    // detent; the rail in the handle is always reachable.
    const [detent, setDetent] = useState('peek');
    const dragControls = useDragControls();
    // Sheet Y at drag start, so we can tell which way the user dragged.
    const dragStartYRef = useRef(0);
    const FOOTER_H = 76;     // global footer nav height (~4.5rem)
    // The peek header height is measured from the DOM (it depends on the
    // critter art), so the sheet rests exactly flush above the footer.
    const peekRef = useRef(null);
    const [peekVisible, setPeekVisible] = useState(212);
    const calcGeom = (vh, pv) => {
        const fullTop = Math.min(Math.max(vh * 0.30, 224), 304); // 14–19rem
        return {
            fullTop,
            detents: {
                full: 0,
                half: vh * 0.54 - fullTop,
                // Rest: only the measured peek header shows, sitting just
                // above the footer — never overlapping it.
                peek: (vh - FOOTER_H - pv) - fullTop,
            },
        };
    };
    const initialGeom = calcGeom(typeof window !== 'undefined' ? window.innerHeight : 800, 212);
    const [fullTop, setFullTop] = useState(initialGeom.fullTop);
    const [detents, setDetents] = useState(initialGeom.detents);
    const sheetY = useMotionValue(initialGeom.detents.peek);
    const SHEET_SPRING = { type: 'spring', stiffness: 380, damping: 38 };

    // Recompute detent geometry on resize and whenever the measured peek
    // header height changes.
    useEffect(() => {
        const recompute = () => {
            const g = calcGeom(window.innerHeight, peekVisible);
            setFullTop(g.fullTop);
            setDetents(g.detents);
        };
        recompute();
        window.addEventListener('resize', recompute);
        return () => window.removeEventListener('resize', recompute);
    }, [peekVisible]);

    // Measure the real peek-header height so it rests flush above the footer.
    useLayoutEffect(() => {
        if (detent !== 'peek' || !peekRef.current) return;
        const measure = () => {
            const h = peekRef.current?.offsetHeight;
            if (h) setPeekVisible(h + 8);
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(peekRef.current);
        return () => ro.disconnect();
    }, [detent]);

    // Animate the sheet whenever the target detent (or geometry) changes.
    useEffect(() => {
        const controls = animate(sheetY, detents[detent], SHEET_SPRING);
        return controls.stop;
    }, [detent, detents.full, detents.half, detents.peek]);

    // Momentum-aware snap. A lower y means the sheet sits higher on screen
    // ('full' === 0), so any upward gesture — released above where it started,
    // or let go with upward momentum — always settles at 'full'; we never
    // pause at 'half' on the way up. Downward gestures snap to the closest
    // detent so the sheet can still collapse back to peek.
    const snapToDetent = (info) => {
        const vy = info?.velocity?.y || 0;
        const proj = sheetY.get() + vy * 0.16;
        const draggedUp = proj < dragStartYRef.current || vy < -20;
        if (draggedUp) {
            setDetent('full');
            animate(sheetY, detents.full, SHEET_SPRING);
            return;
        }
        let best = 'full';
        for (const k of ['full', 'half', 'peek']) {
            if (Math.abs(detents[k] - proj) < Math.abs(detents[best] - proj)) best = k;
        }
        setDetent(best);
        animate(sheetY, detents[best], SHEET_SPRING);
    };

    // Share Profile state — the ShareProfileModal owns its own form state.
    const [showShareProfileModal, setShowShareProfileModal] = useState(false);
    const [isUserMember, setIsUserMember] = useState(false);

    // Edit Profile state — the EditProfileModal owns the form; we just track
    // which member's data to pre-fill it with on open.
    const [showEditProfileModal, setShowEditProfileModal] = useState(false);
    const [memberToEdit, setMemberToEdit] = useState(null);

    // Member Detail Modal state
    const [showMemberDetailModal, setShowMemberDetailModal] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);

    // Favorites state
    const [favoritesLists, setFavoritesLists] = useState([]);

    // Zone Share state
    const [showShareZoneModal, setShowShareZoneModal] = useState(false);
    const [shareLinkCopied, setShareLinkCopied] = useState(false);

    // Zone Chat state
    const [showZoneChatModal, setShowZoneChatModal] = useState(false);

    // Zone Members Modal state (for zones with owner and description)
    const [showZoneMembersModal, setShowZoneMembersModal] = useState(false);

    // Skills & Knowledge state
    const [skillsKnowledge, setSkillsKnowledge] = useState([]);
    const [zoneStores, setZoneStores] = useState([]);
    const [loadingStores, setLoadingStores] = useState(false);
    const [showAddSkillModal, setShowAddSkillModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingSkillId, setEditingSkillId] = useState(null);

    // Zone details state
    const [zoneDescription, setZoneDescription] = useState('');
    const [isZoneOwner, setIsZoneOwner] = useState(false);
    const [hasZoneDescription, setHasZoneDescription] = useState(false);
    const [newSkillData, setNewSkillData] = useState({
        type: 'skill',
        title: '',
        description: '',
        categories: [],
        phone: '',
        hasWhatsApp: false,
        email: '',
        icon: 'Lightbulb'
    });
    const [customSkillCategory, setCustomSkillCategory] = useState('');

    const handleGroupDeleted = async (group_id) => {
        setGroups(prev => prev.filter(group => group.group_id !== group_id));
    };

    // Zone Share handlers
    const getZoneShareUrl = () => {
        const baseUrl = window.location.origin;
        return `${baseUrl}/?view=zone-details&zone=${encodeURIComponent(zone)}`;
    };

    const handleShareZone = () => {
        setShowShareZoneModal(true);
        setShareLinkCopied(false);
    };

    const handleCopyZoneLink = async () => {
        try {
            await navigator.clipboard.writeText(getZoneShareUrl());
            setShareLinkCopied(true);
            setTimeout(() => setShareLinkCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy link:', err);
        }
    };

    const handleShareToWhatsApp = () => {
        const url = getZoneShareUrl();
        const text = `בוא/י להצטרף לקהילה ${zoneName || zone} במקומית!`;
        window.open(`https://wa.me/?text=${encodeURIComponent(text + '\n' + url)}`, '_blank');
    };

    const handleShareToTelegram = () => {
        const url = getZoneShareUrl();
        const text = `בוא/י להצטרף לקהילה ${zoneName || zone} במקומית!`;
        window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
    };

    const handleShareToFacebook = () => {
        const url = getZoneShareUrl();
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
    };

    // Zone details handler
    const fetchZoneDetails = async () => {
        if (!zone || !chatId) return;

        try {
            const res = await cacheSafeFetch(
                `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/get-zone-details?zone_id=${zone}&chat_id=${chatId}`,
                chatId,
                { method: 'GET' },
                { cacheKey: `get-zone-details-${zone}-${chatId}` }
            );
            const data = await res.json();
            setZoneDescription(data.description || '');
            setIsZoneOwner(data.is_owner || false);
            setHasZoneDescription(data.has_description || false);
        } catch (err) {
            console.error('Failed to fetch zone details:', err);
        }
    };

    // Skills & Knowledge handlers
    const fetchSkillsKnowledge = async () => {
        if (!zone) return;

        try {
            const res = await cacheSafeFetch(
                `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/zone-skills-knowledge?zone_id=${zone}`,
                chatId,
                { method: 'GET' },
                { cacheKey: `zone-skills-knowledge-${zone}` }
            );
            const data = await res.json();
            setSkillsKnowledge(data.items || []);
        } catch (err) {
            console.error('Failed to fetch skills & knowledge:', err);
        }
    };

    const handleAddSkill = async () => {
        if (isEditMode) {
            await handleUpdateSkill();
            return;
        }

        if (!chatId || chatId === 'guest') {
            alert('יש להתחבר כדי להוסיף כישורים/ידע');
            return;
        }

        if (!newSkillData.title.trim()) {
            alert('נא להזין כותרת');
            return;
        }

        // Validate contact details - at least one must be provided
        if (!newSkillData.phone?.trim() && !newSkillData.email?.trim()) {
            alert('נא להזין לפחות אמצעי קשר אחד (טלפון או אימייל)');
            return;
        }

        // Validate email format if provided
        if (newSkillData.email?.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(newSkillData.email.trim())) {
                alert('נא להזין כתובת אימייל תקינה');
                return;
            }
        }

        // Validate phone format if provided
        if (newSkillData.phone?.trim()) {
            const phoneRegex = /^0\d{1,2}-?\d{7}$/;
            if (!phoneRegex.test(newSkillData.phone.trim().replace(/-/g, ''))) {
                alert('נא להזין מספר טלפון תקין');
                return;
            }
        }

        // Check if custom category already exists
        if (customSkillCategory.trim() && allCategories.some(cat => cat.toLowerCase() === customSkillCategory.trim().toLowerCase())) {
            alert('קטגוריה זו כבר קיימת - בחר אותה מהרשימה');
            return;
        }

        try {
            // Combine selected categories with custom category
            const finalCategories = [...newSkillData.categories];
            if (customSkillCategory.trim()) {
                finalCategories.push(customSkillCategory.trim());
            }

            const res = await safeFetch(
                'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/add-zone-skill-knowledge',
                chatId,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user_id: chatId,
                        zone_id: zone,
                        type: newSkillData.type,
                        title: newSkillData.title,
                        description: newSkillData.description,
                        category: finalCategories.join(', '),
                        phone: newSkillData.phone?.trim() || '',
                        has_whatsapp: newSkillData.hasWhatsApp || false,
                        email: newSkillData.email?.trim() || '',
                        icon: newSkillData.icon || 'Lightbulb'
                    })
                }
            );

            if (res.ok) {
                setShowAddSkillModal(false);
                setIsEditMode(false);
                setNewSkillData({ type: 'skill', title: '', description: '', categories: [], phone: '', hasWhatsApp: false, email: '', icon: 'Lightbulb' });
                setCustomSkillCategory('');
                // Clear cache before fetching
                delete memoryCache[`zone-skills-knowledge-${zone}`];
                fetchSkillsKnowledge();
            } else {
                alert('שגיאה בהוספת הכישור/ידע');
            }
        } catch (err) {
            console.error('Error adding skill/knowledge:', err);
            alert('שגיאה בהוספת הכישור/ידע');
        }
    };

    const handleEditSkill = (item) => {
        setIsEditMode(true);
        setEditingSkillId(item.entity_id);

        // Parse categories from comma-separated string
        const categories = item.category ? item.category.split(',').map(c => c.trim()) : [];

        setNewSkillData({
            type: item.type,
            title: item.title,
            description: item.description || '',
            categories: categories,
            phone: item.phone || '',
            hasWhatsApp: item.has_whatsapp || false,
            email: item.email || '',
            icon: item.icon || 'Lightbulb'
        });
        setCustomSkillCategory('');
        setShowAddSkillModal(true);
    };

    const handleUpdateSkill = async () => {
        if (!chatId || chatId === 'guest') {
            alert('יש להתחבר כדי לערוך כישורים/ידע');
            return;
        }

        if (!newSkillData.title.trim()) {
            alert('נא להזין כותרת');
            return;
        }

        // Validate email format if provided
        if (newSkillData.email?.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(newSkillData.email.trim())) {
                alert('נא להזין כתובת אימייל תקינה');
                return;
            }
        }

        // Validate phone format if provided
        if (newSkillData.phone?.trim()) {
            const phoneRegex = /^0\d{1,2}-?\d{7}$/;
            if (!phoneRegex.test(newSkillData.phone.trim().replace(/-/g, ''))) {
                alert('נא להזין מספר טלפון תקין');
                return;
            }
        }

        // Check if custom category already exists
        if (customSkillCategory.trim() && allCategories.some(cat => cat.toLowerCase() === customSkillCategory.trim().toLowerCase())) {
            alert('קטגוריה זו כבר קיימת - בחר אותה מהרשימה');
            return;
        }

        try {
            // Combine selected categories with custom category
            const finalCategories = [...newSkillData.categories];
            if (customSkillCategory.trim()) {
                finalCategories.push(customSkillCategory.trim());
            }

            const res = await safeFetch(
                'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/update-zone-skill-knowledge',
                chatId,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user_id: chatId,
                        zone_id: zone,
                        entity_id: editingSkillId,
                        title: newSkillData.title,
                        description: newSkillData.description,
                        category: finalCategories.join(', '),
                        icon: newSkillData.icon || 'Lightbulb'
                    })
                }
            );

            if (res.ok) {
                setShowAddSkillModal(false);
                setIsEditMode(false);
                setEditingSkillId(null);
                setNewSkillData({ type: 'skill', title: '', description: '', categories: [], phone: '', hasWhatsApp: false, email: '', icon: 'Lightbulb' });
                setCustomSkillCategory('');
                // Clear cache before fetching
                delete memoryCache[`zone-skills-knowledge-${zone}`];
                fetchSkillsKnowledge();
            } else {
                alert('שגיאה בעדכון כישורים/ידע');
            }
        } catch (err) {
            console.error('Error updating skill/knowledge:', err);
            alert('שגיאה בעדכון כישורים/ידע');
        }
    };

    const handleDeleteSkill = async (entityId) => {
        if (!confirm('האם אתה בטוח שברצונך למחוק את הפריט?')) return;

        try {
            const res = await safeFetch(
                'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/delete-zone-skill-knowledge',
                chatId,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user_id: chatId,
                        zone_id: zone,
                        entity_id: entityId
                    })
                }
            );

            if (res.ok) {
                // Clear cache before fetching
                delete memoryCache[`zone-skills-knowledge-${zone}`];
                fetchSkillsKnowledge();
            } else {
                alert('שגיאה במחיקת הפריט');
            }
        } catch (err) {
            console.error('Error deleting skill/knowledge:', err);
            alert('שגיאה במחיקת הפריט');
        }
    };

    // Fetch user's favorites lists
    const fetchFavoritesLists = async () => {
        if (!chatId || chatId === 'guest') return;

        try {
            const res = await cacheSafeFetch(
                `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/user-favorites-lists?chat_id=${chatId}`,
                chatId,
                { method: 'GET' },
                { cacheKey: `user-favorites-lists-${chatId}` }
            );
            const data = await res.json();
            const parsedLists = (data || []).map(list => ({
                ...list,
                data: typeof list.data === 'string' ? JSON.parse(list.data) : list.data
            }));
            setFavoritesLists(parsedLists);
        } catch (err) {
            console.error('❌ Failed to fetch favorites lists:', err);
        }
    };

    // Toggle entity in favorites list
    const handleToggleEntityInList = async (listId, entityId, entityType) => {
        try {
            const list = favoritesLists.find(l => l.playlist_id === listId);
            const items = list?.data?.[entityType] || [];

            // Check if entity is in list (handle both simple IDs and complex objects)
            let isInList;
            if (entityType === 'groups') {
                isInList = items.some(item => item.group_id === entityId);
            } else if (entityType === 'posts') {
                isInList = items.some(item => item.post_id === entityId);
            } else {
                isInList = items.includes(entityId);
            }

            const action = isInList ? 'remove' : 'add';

            await safeFetch(
                'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/update-favorites-list',
                chatId,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        chat_id: chatId,
                        list_id: listId,
                        entity_type: entityType,
                        entity_id: entityId,
                        action: action,
                        zone_id: zone
                    })
                }
            );

            // Update local state (handle both simple IDs and complex objects)
            setFavoritesLists(prev =>
                prev.map(l =>
                    l.playlist_id === listId
                        ? {
                            ...l,
                            data: {
                                ...l.data,
                                [entityType]: (() => {
                                    if (entityType === 'groups') {
                                        return isInList
                                            ? items.filter(item => item.group_id !== entityId)
                                            : [...items, { group_id: entityId, zone_id: zone }];
                                    } else if (entityType === 'posts') {
                                        return isInList
                                            ? items.filter(item => item.post_id !== entityId)
                                            : [...items, { post_id: entityId, zone_id: zone }];
                                    } else {
                                        return isInList
                                            ? items.filter(id => id !== entityId)
                                            : [...items, entityId];
                                    }
                                })()
                            }
                        }
                        : l
                )
            );
        } catch (err) {
            console.error('❌ Failed to toggle entity in list:', err);
            alert('❌ שגיאה בעדכון רשימת המועדפים');
        }
    };

    // Create new favorites list with entity
    const handleCreateEntityList = async (listName, entityType, entityId) => {
        const newList = {
            playlist_id: `temp-${Date.now()}`,
            data: {
                name: listName,
                businesses: [],
                events: [],
                posts: [],
                zones: [],
                groups: []
            }
        };

        try {
            const res = await safeFetch(
                'https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/create-user-favorites-list',
                chatId,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        chat_id: chatId,
                        entity_type: entityType,
                        entity_id: entityId,
                        name: listName,
                        zone_id: zone
                    })
                }
            );

            const data = await res.json();
            newList.playlist_id = data.list_id;

            // Set the entity in the new list (handle both simple IDs and complex objects)
            if (entityType === 'groups') {
                newList.data[entityType] = [{ group_id: entityId, zone_id: zone }];
            } else if (entityType === 'posts') {
                newList.data[entityType] = [{ post_id: entityId, zone_id: zone }];
            } else {
                newList.data[entityType] = [entityId];
            }

            setFavoritesLists(prev => [...prev, newList]);
        } catch (err) {
            console.error('❌ Failed to create favorites list:', err);
            alert('❌ שגיאה ביצירת רשימת מועדפים');
        }
    };


    useEffect(() => {
        const fetchGroups = async () => {
            const res = await cacheSafeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/get-zone-groups?zone_id=${zone}`, chatId, { method: 'GET' }, { cacheKey: `get-zone-groups-${zone}-${chatId}` });
            const data = await res.json();
            setGroups(data || []);
        };

        fetchGroups();
    }, [zone, refreshKey]); // <- refreshKey added here

    // Fetch every zone in the network so the map can show the other zones too.
    // When the user zooms out from their zone they can pick another one directly.
    useEffect(() => {
        const fetchAllZones = async () => {
            try {
                const res = await cacheSafeFetch(
                    `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/user-locals?chat_id=${chatId}`,
                    chatId,
                    { method: 'GET' },
                    { cacheKey: `user-locals-${chatId}` }
                );
                const data = await res.json();
                const markers = (data.all_zones || [])
                    .map((z) => {
                        if (!z.location) return null;
                        try {
                            const parsed = JSON.parse(z.location);
                            const name = z.zone_display_name || z.zone_id;
                            const id = z.zone_id;
                            if (Array.isArray(parsed) && parsed.length > 0 && parsed[0]) {
                                return { name, id, lat: parsed[0].latitude, lng: parsed[0].longitude };
                            }
                            if (parsed && parsed.latitude && parsed.longitude) {
                                return { name, id, lat: parsed.latitude, lng: parsed.longitude };
                            }
                        } catch { return null; }
                        return null;
                    })
                    .filter(Boolean)
                    // Drop the zone we're already viewing — its entities are shown instead.
                    .filter((m) => m.id !== zone && m.name !== zone);
                setOtherZones(markers);
            } catch (err) {
                console.error('❌ Failed to load zones for map:', err);
            }
        };

        fetchAllZones();
    }, [zone, chatId]);

    useEffect(() => {
        if (!showCreateGroupModal && !showAddSkillModal) return;

        const loadCategories = async () => {
            try {
                const res = await cacheSafeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/zones-and-categories`, chatId, { method: 'GET' }, { cacheKey: `zones-and-categories-${chatId}` });
                const data = await res.json();
                const sortedCategories = (data.categories || []).sort((a, b) => a.localeCompare(b, 'he'));
                setAllCategories(sortedCategories);
            } catch (err) {
                console.error('❌ Failed to load categories:', err);
            }
        };

        loadCategories();
    }, [showCreateGroupModal, showAddSkillModal]);

    // Removed duplicate useEffect that fetched groups and caused double businesses?zone_id call


    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const highlightId = urlParams.get('highlight_post');
        const tab = urlParams.get('tab');

        if (tab === 'community') {
            setActiveTab('community');
        }

        if (highlightId) {
            setTimeout(() => {
                const el = document.getElementById(`post-${highlightId}`);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });

                    // 👇 Remove highlight_post param from URL
                    const newParams = new URLSearchParams(window.location.search);
                    newParams.delete('highlight_post');
                    const newUrl = `${window.location.pathname}?${newParams.toString()}`;
                    window.history.replaceState({}, '', newUrl);
                }
            }, 500);
        }
    }, [communityPosts]);

    const fetchPostComments = async (postId) => {


        try {
            const res = await safeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/post-comments?post_id=${postId}`, chatId);
            if (!res.ok) {
                const text = await res.text();
                console.error('❌ Failed to fetch comments:', text);
                alert('❌ שגיאה בטעינת תגובות');
                return;
            }

            const raw = await res.json();
            const comments = Array.isArray(raw) ? raw.flat() : [];

            setPostComments((prev) => ({
                ...prev,
                [postId]: comments,
            }));

            setOpenCommentsPostId(postId);
        } catch (err) {
            console.error('❌ Fetch comments error:', err);
            alert('❌ שגיאה בטעינת תגובות');
        }
    };

    const handleToggleEmojiPanel = (postId) => {
        setOpenEmojiPanel((prev) => (prev === postId ? null : postId));
        setOpenCommentPanel(null); // always close the comment panel
    };

    const handleToggleCommentPanel = (postId) => {
        setOpenCommentPanel((prev) => (prev === postId ? null : postId));
        setOpenEmojiPanel(null); // always close the emoji panel
    };

    const handleDeletePost = async (postId) => {
        if (!window.confirm('האם אתה בטוח שברצונך למחוק את הפוסט?')) return;

        try {
            const res = await safeFetch('https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/delete-post', chatId, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ post_id: postId, chat_id: chatId, zone_id: zone }),
            });

            if (res.ok) {
                setCommunityPosts((prev) => prev.filter((p) => p.post_id !== postId));
                //alert('✅ הפוסט נמחק בהצלחה');
            } else {
                const errorText = await res.text();
                console.error('Delete failed:', errorText);
                alert('❌ מחיקה נכשלה');
            }
        } catch (err) {
            console.error('Delete error:', err);
            alert('❌ שגיאה במחיקה');
        }
    };

    const fetchZoneData = async () => {
        try {
            setLoading(true);

            const [catRes, bizRes, evtRes, barRes, postsRes] = await Promise.all([
                cacheSafeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/user-zone-categories?chat_id=${chatId}&zone_id=${zone}`, chatId, { method: 'GET' }, { cacheKey: `user-zone-categories-${zone}-${chatId}` }),
                cacheSafeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/businesses?zone_id=${zone}&type=business&chat_id=${chatId}`, chatId, { method: 'GET' }, { cacheKey: `businesses-type-business-${zone}-${chatId}` }),
                cacheSafeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/businesses?zone_id=${zone}&type=event&chat_id=${chatId}`, chatId, { method: 'GET' }, { cacheKey: `businesses-type-event-${zone}-${chatId}` }),
                cacheSafeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/businesses?zone_id=${zone}&type=barter&chat_id=${chatId}`, chatId, { method: 'GET' }, { cacheKey: `businesses-type-barter-${zone}-${chatId}` }),
                // cacheSafeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/zone-posts?zone_id=${zone}&chat_id=${chatId}`, chatId, { method: 'GET' }, { cacheKey: `zone-posts-${zone}-${chatId}` })
            ]);

            const catData = await catRes.json();
            const bizData = await bizRes.json();
            const evtData = await evtRes.json();
            const barData = await barRes.json();
            // const postsData = await postsRes.json();

            // setCommunityPosts(postsData || []);
            setCategoryGroups(catData?.category_groups || {});
            setCategories(catData?.available_categories || []);
            setEventsCategories(catData?.available_events_categories || []);
            setBartersCategories(catData?.available_barters_categories || []);
            setGroupsCategories(catData?.available_groups_categories || []);

            // Set zone display name from API response
            if (catData?.zone_display_name) {
                setZoneName(catData.zone_display_name);
            }

            // Try to extract zone center from catData.zone_location or catData.zone_location_json or similar
            let center = null;
            if (catData && catData.zone_location) {
                try {
                    // zone_location may be a JSON string or object
                    let loc = catData.zone_location;
                    if (typeof loc === 'string') loc = JSON.parse(loc);
                    if (Array.isArray(loc) && loc.length > 0 && loc[0]) {
                        center = [loc[0].latitude, loc[0].longitude];
                    } else if (loc && loc.latitude && loc.longitude) {
                        center = [loc.latitude, loc.longitude];
                    }
                } catch (e) {
                    // fallback below
                }
            }
            // Fallback: try to infer from businesses if not found
            if (!center && bizData && Array.isArray(bizData.businesses) && bizData.businesses.length > 0) {
                try {
                    const first = bizData.businesses[0];
                    if (first.location) {
                        const loc = JSON.parse(first.location);
                        if (loc && loc.lat && loc.lng) {
                            center = [loc.lat, loc.lng];
                        }
                    }
                } catch { }
            }
            setZoneCenter(center);

            // The businesses endpoint can return a non-array payload (error
            // object, empty body, cache miss). Treat anything that isn't an
            // array as "no items" rather than crashing the whole zone load.
            const byRating = (a, b) => {
                if (b.avg_rating === 0 && a.avg_rating === 0) return 0;
                if (b.avg_rating === 0) return -1;
                if (a.avg_rating === 0) return 1;
                return b.avg_rating - a.avg_rating;
            };

            const sortedBusinesses = (Array.isArray(bizData?.businesses) ? bizData.businesses : []).slice().sort(byRating);
            const sortedEvents = (Array.isArray(evtData?.businesses) ? evtData.businesses : []).slice().sort(byRating);
            const sortedBarters = (Array.isArray(barData?.businesses) ? barData.businesses : []).slice().sort(byRating);

            setBusinesses(sortedBusinesses || []);
            setEvents(sortedEvents || []);
            setBarters(sortedBarters || []);

            // Fetch store details for businesses that have stores
            const storeBusinesses = sortedBusinesses.filter(b => (b.has_store === true || b.has_store === 'true') && b.store_id);
            console.log('🏪 Businesses with stores:', storeBusinesses.length, storeBusinesses.map(b => ({ business_id: b.business_id, business_name: b.business_name, has_store: b.has_store, store_id: b.store_id, store_slug: b.store_slug })));
            if (storeBusinesses.length > 0) {
                setLoadingStores(true);
                const storeApiUrl = process.env.REACT_APP_STORE_API_URL || 'https://v3ugnvc3qk.execute-api.us-west-2.amazonaws.com/prod';
                const storeResults = await Promise.all(
                    storeBusinesses.map(async (biz) => {
                        try {
                            const res = await fetch(`${storeApiUrl}/get-store-by-id?store_id=${biz.store_id}`);
                            if (!res.ok) {
                                console.error(`Store API returned ${res.status} for store_id=${biz.store_id}`);
                                return null;
                            }
                            const data = await res.json();
                            console.log('🏪 Store data for', biz.store_id, ':', data);
                            return { ...data, _business: biz };
                        } catch (err) {
                            console.error(`Failed to fetch store ${biz.store_id}:`, err);
                            return null;
                        }
                    })
                );
                setZoneStores(storeResults.filter(s => s !== null));
                setLoadingStores(false);
            }
        } catch (err) {
            console.error('❌ Error loading zone data:', err);
            setError('שגיאה בטעינת נתוני האזור');
        } finally {
            setLoading(false);
        }
    };

    // const fetchPosts = async () => {
    //     try {
    //         setLoading(true);

    //         const [postsRes] = await Promise.all([
    //             safeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/zone-posts?zone_id=${zone}&chat_id=${chatId}`)
    //         ]);
    //         const postsData = await postsRes.json();

    //         setCommunityPosts(postsData || []);

    //     } catch (err) {
    //         console.error('❌ Error loading zone data:', err);
    //         setError('שגיאה בטעינת נתוני האזור');
    //     } finally {
    //         setLoading(false);
    //     }
    // };


    useEffect(() => {
        fetchZoneData();
        fetchCommunityMembers();
        fetchFavoritesLists();
        fetchSkillsKnowledge();
        fetchZoneDetails();
    }, [zone, chatId]);

    // Effect to set default tab to 'about' if zone has description
    useEffect(() => {
        if (hasZoneDescription && !hasSetInitialTab.current) {
            hasSetInitialTab.current = true;
            if (externalOnTabChange) {
                externalOnTabChange('about');
            } else {
                setInternalActiveTab('about');
            }
        }
    }, [hasZoneDescription, externalOnTabChange]);

    // When the zone has entities but none of them carry coordinates, the map
    // behind the sheet stays empty — so lift the sheet to its 'full' detent on
    // first load, surfacing the section tabs instead of a blank map. Runs once;
    // never fights the user's own drags afterwards.
    useEffect(() => {
        if (loading || hasSetInitialDetent.current) return;

        const mappableItems = [
            ...(businesses || []),
            ...(events || []),
            ...(barters || []),
            ...((zoneStores || []).map(s => s._business).filter(Boolean)),
        ];
        const totalEntities =
            (businesses?.length || 0) +
            (events?.length || 0) +
            (barters?.length || 0) +
            (zoneStores?.length || 0);

        if (totalEntities === 0) {
            hasSetInitialDetent.current = true; // empty zone → keep the banner visible
            return;
        }

        hasSetInitialDetent.current = true;
        const anyOnMap = mappableItems.some(it => parseEntityLatLng(it.location));
        if (!anyOnMap) setDetent('full');
    }, [loading, businesses, events, barters, zoneStores]);

    // Effect to set viewMode to 'compact' if groups tab has more than 4 items
    useEffect(() => {
        if (activeTab === 'groups') {
            const groupCount = groups?.length || 0;

            // Set to compact if more than 4 groups, otherwise default to rolodex
            if (groupCount > 4) {
                setViewMode('compact');
            } else {
                setViewMode('rolodex');
            }
        }
    }, [activeTab, groups]);

    // "What's Happening Today" popup removed for now.

    const fetchCommunityMembers = async (forceRefresh = false) => {
        try {
            const res = await cacheSafeFetch(`https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/zone-members?zone_id=${zone}&chat_id=${chatId}`, chatId, { method: 'GET' }, { cacheKey: `zone-members-${zone}-${chatId}`, forceRefresh });
            const data = await res.json();
            const members = data?.members || [];
            setCommunityMembers(members);

            // Use the is_current_user_member flag from the API response
            setIsUserMember(data?.is_current_user_member || false);
            setCurrentUserMember(data?.current_user_member || null);
        } catch (err) {
            console.error('❌ Error loading community members:', err);
        }
    };

    const groupedCategoryGroups = Array.isArray(categoryGroups) ? categoryGroups.reduce((acc, group) => {
        const baseCategory = group.group_category.replace(' וואטסאפ', '');
        if (!acc[baseCategory]) acc[baseCategory] = {};
        if (group.group_url.includes('whatsapp')) {
            acc[baseCategory].whatsapp = group.group_url;
        } else {
            acc[baseCategory].telegram = group.group_url;
        }
        return acc;
    }, {}) : {};

    if (error) return <p className="text-danger-500 text-center">{error}</p>;

    return (
        <div className="relative" style={{ minHeight: '100dvh', background: 'var(--color-bg-base)', fontFamily: 'var(--font-family-body)' }}>
            {/* Full-page immersive map — fixed background spanning the entire viewport */}
            <div className="fixed inset-0 z-0">
                <ZoneMapHero
                    fullScreen
                    loading={loading}
                    zoneName={zoneName || zone}
                    memberCount={communityMembers?.length || 0}
                    businesses={businesses || []}
                    events={events || []}
                    barters={barters || []}
                    stores={zoneStores || []}
                    otherZones={otherZones}
                    zoneCenter={zoneCenter}
                    onShareClick={handleShareZone}
                    onChatClick={() => setShowZoneChatModal(true)}
                    onPinClick={(item) => {
                        const id = item.business_id || item.id;
                        if (id) navigate(`?entity=${id}`);
                    }}
                />
            </div>

            {/* Content sheet — one draggable surface with three detents
                (peek · half · full). Drag (or tap) the handle to move between
                them; the map stays alive behind it the whole time. Body
                scrolls internally; only the handle initiates a drag, so
                scrolling the list never fights the sheet gesture. */}
            <motion.div
                drag="y"
                dragListener={false}
                dragControls={dragControls}
                dragConstraints={{ top: detents.full, bottom: detents.peek }}
                dragElastic={0.05}
                onDragStart={() => { dragStartYRef.current = sheetY.get(); }}
                onDragEnd={(_, info) => snapToDetent(info)}
                style={{ y: sheetY, top: fullTop, bottom: 'calc(4.5rem + env(safe-area-inset-bottom, 0px))' }}
                className="fixed inset-x-0 z-30 overflow-y-auto overscroll-contain"
            >
                        <div className="relative max-w-6xl mx-auto px-3 sm:px-5 pb-10">
                            <section
                                aria-label="תוכן הקהילה"
                                className="relative bg-surface rounded-t-[2rem] sm:rounded-t-[2.5rem] rounded-b-2xl ring-1 ring-line-subtle shadow-elevated"
                            >
                                {/* Decorative top-cap — clipped to its own bounds. */}
                                <div aria-hidden="true" className="absolute inset-x-0 top-0 h-32 rounded-t-[2rem] sm:rounded-t-[2.5rem] overflow-hidden pointer-events-none">
                                    {/* Thin gradient accent bar */}
                                    <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-brand-400 via-brand-500 to-brand-600" />
                                    {/* Primary blob — leading corner */}
                                    <div className="absolute -top-10 -start-12 w-44 h-44 rounded-pill blur-3xl opacity-50 bg-brand-200/70" />
                                    {/* Secondary blob — trailing corner, complementary tone */}
                                    <div className="absolute -top-6 -end-12 w-36 h-36 rounded-pill blur-3xl opacity-30 bg-secondary-200/60" />
                                </div>

                                {/* Sheet header — the only region that initiates a
                                    sheet drag, and the only thing visible at rest:
                                    a grab handle with a "dive in" invitation. Once
                                    lifted, the invitation gives way to the section
                                    tabs. Sticks while the body scrolls. */}
                                <div
                                    onPointerDown={(e) => dragControls.start(e)}
                                    style={{ touchAction: 'none' }}
                                    className="sticky top-0 z-20 rounded-t-[2rem] sm:rounded-t-[2.5rem] bg-surface/85 backdrop-blur-xl backdrop-saturate-150 cursor-grab active:cursor-grabbing select-none"
                                >
                                    {detent === 'peek' ? (
                                        <div ref={peekRef} className="flex flex-col items-center pt-3 pb-4">
                                            <motion.button
                                                type="button"
                                                aria-label="גרירה להרחבה"
                                                onClick={() => setDetent('half')}
                                                className="block h-1.5 w-11 rounded-pill bg-line hover:bg-line-strong transition-colors"
                                                animate={{ y: [0, -3, 0] }}
                                                transition={{ duration: 1.7, repeat: Infinity, ease: 'easeInOut' }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setDetent('half')}
                                                className="mt-3 text-sm font-bold text-ink-muted hover:text-ink transition-colors"
                                            >
                                                צלול פנימה
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                aria-label="גרירה להרחבה או צמצום"
                                                onClick={() => setDetent(d => (d === 'full' ? 'peek' : 'full'))}
                                                className="block mx-auto h-1.5 w-11 rounded-pill bg-line hover:bg-line-strong transition-colors mt-3 mb-2"
                                            />
                                            <div className="overflow-x-auto px-3 sm:px-5 pb-2.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                                                <ZoneSectionNav
                                                    variant="floating"
                                                    activeTab={activeTab}
                                                    onTabChange={(id) => { setActiveTab(id); setDetent(d => (d === 'peek' ? 'half' : d)); }}
                                                    hasAbout={hasZoneDescription}
                                                    counts={{
                                                        businesses: businesses?.length || 0,
                                                        stores:     zoneStores?.length || 0,
                                                        events:     events?.length || 0,
                                                        barters:    barters?.length || 0,
                                                        members:    communityMembers?.length || 0,
                                                        skills:     skillsKnowledge?.length || 0,
                                                        groups:     groups?.length || 0,
                                                    }}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>

                    {/* Tab content area — the section rail now lives in the
                        sticky sheet header above. */}
                    <div className="relative px-4 sm:px-7 lg:px-9 pt-3 sm:pt-4 pb-6 sm:pb-8 lg:pb-10">
                        {activeTab === 'events' && (
                            <EntityListTab
                                infoIcon={CalendarDays}
                                infoTitle="מפגשים ואירועים"
                                infoDescription="כאן תוכלו לשתף אירועים מכל סוג עם הקהילה - אם יש לזה תאריך, שתפו את זה כאן!"
                                infoCollapsed={collapsedInfoBoxes.events}
                                onToggleInfo={() => setCollapsedInfoBoxes(prev => ({ ...prev, events: !prev.events }))}
                                addLabel="הוספת מפגש חדש"
                                isLoggedIn={chatId !== null && chatId !== 'guest'}
                                showCreateModal={showCreateEventModal}
                                onOpenCreate={() => setShowCreateEventModal(true)}
                                onCloseCreate={() => setShowCreateEventModal(false)}
                                entityType="event"
                            rolodexType="event"
                                viewMode={viewMode}
                                onViewModeChange={setViewMode}
                                showFilters={showFilters}
                                onToggleFilters={() => setShowFilters(prev => !prev)}
                                activeFilterCount={activeFilterCount}
                                onFilterChange={setActiveFilterCount}
                                items={events}
                                categories={eventsCategories}
                                zone={zone}
                                zoneCenter={zoneCenter}
                                chatId={chatId}
                                favoritesLists={favoritesLists}
                                onToggleFavorite={handleToggleEntityInList}
                                onCreateFavoriteList={handleCreateEntityList}
                            />
                        )}

                        {activeTab === 'barters' && (
                            <EntityListTab
                                infoIcon={Handshake}
                                infoTitle="בארטרים"
                                infoDescription="הבארטר הוא דרך נפלאה לקהילה לתמוך ולעזור אחד לשני ללא כסף - חליפין של שירותים, מוצרים וידע לחיזוק הקהילה"
                                infoCollapsed={collapsedInfoBoxes.barters}
                                onToggleInfo={() => setCollapsedInfoBoxes(prev => ({ ...prev, barters: !prev.barters }))}
                                addLabel="הוספת בארטר חדש"
                                isLoggedIn={chatId !== null && chatId !== 'guest'}
                                showCreateModal={showCreateBarterModal}
                                onOpenCreate={() => setShowCreateBarterModal(true)}
                                onCloseCreate={() => setShowCreateBarterModal(false)}
                                entityType="barter"
                            rolodexType="barter"
                                viewMode={viewMode}
                                onViewModeChange={setViewMode}
                                showFilters={showFilters}
                                onToggleFilters={() => setShowFilters(prev => !prev)}
                                activeFilterCount={activeFilterCount}
                                onFilterChange={setActiveFilterCount}
                                items={barters}
                                categories={bartersCategories}
                                zone={zone}
                                zoneCenter={zoneCenter}
                                chatId={chatId}
                                favoritesLists={favoritesLists}
                                onToggleFavorite={handleToggleEntityInList}
                                onCreateFavoriteList={handleCreateEntityList}
                            />
                        )}

                        {activeTab === 'about' && hasZoneDescription && (
                            <AboutTab
                                zoneDescription={zoneDescription}
                                onOpenMembers={() => setShowZoneMembersModal(true)}
                            />
                        )}

                        {activeTab === 'businesses' && (
                            <EntityListTab
                                infoIcon={Store}
                                infoTitle="עסקים מקומיים"
                                infoDescription="שתפו את העסקים שלכם עם הקהילה ותמכו בכלכלה המקומית - ביחד אנחנו חזקים יותר!"
                                infoCollapsed={collapsedInfoBoxes.businesses}
                                onToggleInfo={() => setCollapsedInfoBoxes(prev => ({ ...prev, businesses: !prev.businesses }))}
                                addLabel="הוספת עסק חדש"
                                isLoggedIn={chatId !== null && chatId !== 'guest'}
                                showCreateModal={showCreateBusinessModal}
                                onOpenCreate={() => setShowCreateBusinessModal(true)}
                                onCloseCreate={() => setShowCreateBusinessModal(false)}
                                entityType="business"
                                viewMode={viewMode}
                                onViewModeChange={setViewMode}
                                showFilters={showFilters}
                                onToggleFilters={() => setShowFilters(prev => !prev)}
                                activeFilterCount={activeFilterCount}
                                onFilterChange={setActiveFilterCount}
                                items={businesses}
                                categories={categories}
                                zone={zone}
                                zoneCenter={zoneCenter}
                                chatId={chatId}
                                favoritesLists={favoritesLists}
                                onToggleFavorite={handleToggleEntityInList}
                                onCreateFavoriteList={handleCreateEntityList}
                            />
                        )}

                        {activeTab === 'stores' && (
                            <StoresTab
                                stores={zoneStores}
                                loading={loadingStores}
                                infoCollapsed={collapsedInfoBoxes.stores}
                                onToggleInfo={() => setCollapsedInfoBoxes(prev => ({ ...prev, stores: !prev.stores }))}
                            />
                        )}

                        {activeTab === 'members' && (
                            <div className="space-y-6">
                                {/* Section header */}
                                <div className="flex items-end justify-between gap-4 flex-wrap">
                                    <div>
                                        <p className="eyebrow text-brand-700 mb-1">אנשים</p>
                                        <h2 className="heading-lg text-ink">חברי הקהילה</h2>
                                        <p className="text-sm text-ink-muted mt-1 max-w-md">
                                            הכירו את האנשים שמרכיבים את הקהילה — לחצו על פרופיל לפרטים, או שתפו את שלכם.
                                        </p>
                                    </div>
                                    {(chatId !== null && chatId !== 'guest') && !isUserMember && (
                                        <motion.button
                                            onClick={() => setShowShareProfileModal(true)}
                                            whileHover={{ y: -1 }}
                                            whileTap={{ scale: 0.97 }}
                                            className="inline-flex items-center gap-2 h-11 px-5 rounded-pill bg-brand-500 hover:bg-brand-600 text-ink-inverse font-bold text-sm shadow-card hover:shadow-elevated transition-shadow"
                                        >
                                            <UserCircle size={18} />
                                            <span>שתפו את הפרופיל שלכם</span>
                                        </motion.button>
                                    )}
                                </div>

                                {/* Members Grid */}
                                {loading ? (
                                    <div className="flex flex-col items-center justify-center py-20">
                                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-100 border-t-brand-500 mb-4" />
                                        <p className="text-ink-muted font-semibold">טוען חברי קהילה...</p>
                                    </div>
                                ) : communityMembers.length > 0 ? (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5 sm:gap-3">
                                        {(() => {
                                            // Sort members to show own profile first (backend provides own_profile flag)
                                            const sortedMembers = [...communityMembers].sort((a, b) => {
                                                if (a.own_profile) return -1;
                                                if (b.own_profile) return 1;
                                                return 0;
                                            });

                                            // Rotating accent palette — mirrors the home page bento cards.
                                            // Uses only shades defined in tailwind.config.js (status colors expose 50/100/500/600/700).
                                            const ACCENTS = [
                                                { gradient: 'from-brand-400 to-brand-600',         blob: 'bg-brand-200/50' },
                                                { gradient: 'from-warning-500 to-warning-600',     blob: 'bg-warning-100/70' },
                                                { gradient: 'from-info-500 to-info-600',           blob: 'bg-info-100/70' },
                                                { gradient: 'from-success-500 to-success-600',     blob: 'bg-success-100/70' },
                                                { gradient: 'from-secondary-400 to-secondary-600', blob: 'bg-secondary-200/50' },
                                                { gradient: 'from-danger-500 to-danger-600',       blob: 'bg-danger-100/70' },
                                            ];

                                            return sortedMembers.map((member, idx) => {
                                                const isCurrentUser = member.own_profile === true;
                                                const accent = isCurrentUser
                                                    ? { gradient: 'from-brand-500 to-brand-700', blob: 'bg-brand-300/50' }
                                                    : ACCENTS[idx % ACCENTS.length];

                                                const openDetails = async () => {
                                                    try {
                                                        const res = await safeFetch('https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/get-member-details', chatId, {
                                                            method: 'POST',
                                                            headers: { 'Content-Type': 'application/json' },
                                                            body: JSON.stringify({
                                                                encrypted_user_id: member.encrypted_user_id,
                                                                zone_id: zone,
                                                            }),
                                                        });
                                                        if (res.ok) {
                                                            const fullMemberData = await res.json();
                                                            setSelectedMember(fullMemberData);
                                                            setShowMemberDetailModal(true);
                                                        } else {
                                                            console.error('Failed to fetch member details');
                                                            alert('❌ שגיאה בטעינת פרטי חבר');
                                                        }
                                                    } catch (err) {
                                                        console.error('Error fetching member details:', err);
                                                        alert('❌ שגיאה בטעינת פרטי חבר');
                                                    }
                                                };

                                                return (
                                                    <motion.div
                                                        key={idx}
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={openDetails}
                                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDetails(); } }}
                                                        initial={{ opacity: 0, y: 8 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ duration: 0.3, delay: Math.min(idx * 0.03, 0.3), ease: [0.22, 1, 0.36, 1] }}
                                                        whileHover={{ y: -2 }}
                                                        whileTap={{ scale: 0.98 }}
                                                        className={`group relative overflow-hidden rounded-2xl bg-surface border ${isCurrentUser ? 'border-brand-200 ring-1 ring-brand-100' : 'border-line-subtle hover:border-line'} shadow-card hover:shadow-elevated transition-shadow duration-300 p-4 flex flex-col items-center text-center cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2`}
                                                    >
                                                        <div aria-hidden="true" className={`pointer-events-none absolute -top-10 -start-8 w-24 h-24 rounded-pill blur-2xl opacity-60 ${accent.blob}`} />

                                                        {isCurrentUser && (
                                                            <span className="absolute top-2 end-2 inline-flex items-center text-2xs font-bold px-2 py-0.5 rounded-pill bg-brand-50 text-brand-700 ring-1 ring-brand-200 z-10">
                                                                אתם
                                                            </span>
                                                        )}

                                                        <div className="relative mb-3">
                                                            <div className={`w-16 h-16 sm:w-[68px] sm:h-[68px] rounded-pill overflow-hidden ring-4 ring-surface bg-gradient-to-br ${accent.gradient} shadow-sm flex items-center justify-center`}>
                                                                {member.photo_url ? (
                                                                    <img
                                                                        src={member.photo_url}
                                                                        alt={member.name || 'Member'}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                ) : (
                                                                    <UserCircle size={32} className="text-white" />
                                                                )}
                                                            </div>

                                                            {/* Edit/Remove — visible only on own card */}
                                                            {isCurrentUser && (chatId !== null && chatId !== 'guest') && (
                                                                <div className="absolute -top-1 -end-1 flex flex-col gap-1">
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setMemberToEdit(member || currentUserMember);
                                                                            setShowEditProfileModal(true);
                                                                        }}
                                                                        className="w-7 h-7 rounded-pill flex items-center justify-center bg-surface ring-1 ring-line text-ink-muted hover:text-brand-700 hover:ring-brand-200 shadow-sm transition-all active:scale-95"
                                                                        title="ערוך פרופיל"
                                                                    >
                                                                        <Edit size={13} />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={async (e) => {
                                                                            e.stopPropagation();
                                                                            if (confirm('האם אתה בטוח שברצונך להסיר את הפרופיל שלך מהקהילה?')) {
                                                                                try {
                                                                                    const res = await safeFetch('https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/remove-user-from-zone', chatId, {
                                                                                        method: 'POST',
                                                                                        headers: { 'Content-Type': 'application/json' },
                                                                                        body: JSON.stringify({
                                                                                            user_id: chatId,
                                                                                            zone_id: zone,
                                                                                        }),
                                                                                    });
                                                                                    if (res.ok) {
                                                                                        alert('✅ הפרופיל שלך הוסר מהאזור');
                                                                                        await fetchCommunityMembers(true);
                                                                                    } else {
                                                                                        alert('❌ הסרת הפרופיל נכשלה');
                                                                                    }
                                                                                } catch (err) {
                                                                                    console.error('Remove profile error:', err);
                                                                                    alert('❌ שגיאה בהסרת הפרופיל');
                                                                                }
                                                                            }
                                                                        }}
                                                                        className="w-7 h-7 rounded-pill flex items-center justify-center bg-surface ring-1 ring-line text-ink-muted hover:text-danger-600 hover:ring-danger-100 shadow-sm transition-all active:scale-95"
                                                                        title="הסר מהקהילה"
                                                                    >
                                                                        <Trash2 size={13} />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <p className="relative text-sm font-bold text-ink-strong leading-tight line-clamp-2 px-1">
                                                            {member.name || 'חבר קהילה'}
                                                        </p>
                                                    </motion.div>
                                                );
                                            });
                                        })()}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center text-center py-16 px-6 surface-soft rounded-2xl max-w-2xl mx-auto">
                                        <div className="relative mb-5">
                                            <span aria-hidden="true" className="absolute inset-0 bg-brand-200/40 rounded-pill blur-2xl" />
                                            <span className="relative inline-flex items-center justify-center w-20 h-20 rounded-pill bg-surface ring-4 ring-brand-50">
                                                <UserCircle className="w-9 h-9 text-brand-500" strokeWidth={1.75} />
                                            </span>
                                        </div>
                                        <h3 className="heading-sm text-ink mb-2">אין עדיין חברי קהילה</h3>
                                        <p className="text-sm text-ink-muted max-w-xs">היו הראשונים להצטרף לקהילה!</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'community' && (
                            <div className="space-y-6">
                                {/* Create Post Button — confident primary CTA */}
                                {(chatId !== null && chatId !== 'guest') && (
                                    <div className="flex justify-center">
                                        <motion.button
                                            type="button"
                                            onClick={() => setShowPostOptions(true)}
                                            whileHover={{ y: -2 }}
                                            whileTap={{ scale: 0.98 }}
                                            transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
                                            className="group inline-flex items-center gap-2.5 px-7 py-3 rounded-pill bg-gradient-to-br from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 text-ink-inverse font-bold shadow-card hover:shadow-elevated transition-shadow duration-300 ease-smooth focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-base"
                                        >
                                            <Plus className="w-4 h-4" strokeWidth={2.75} />
                                            <span>שתפו עם הקהילה</span>
                                        </motion.button>
                                    </div>
                                )}

                                {showPostOptions && (
                                    <div
                                        className="fixed inset-0 bg-ink/45 backdrop-blur-sm z-modal flex items-center justify-center p-4 animate-fade-in"
                                        onClick={() => setShowPostOptions(false)}
                                    >
                                        <motion.div
                                            initial={{ opacity: 0, scale: 0.96, y: 12 }}
                                            animate={{ opacity: 1, scale: 1, y: 0 }}
                                            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                                            onClick={(e) => e.stopPropagation()}
                                            className="bg-surface rounded-2xl shadow-elevated border border-line-subtle p-6 sm:p-7 w-full max-w-md"
                                        >
                                            <div className="text-center mb-5">
                                                <h3 className="heading-md mb-1">מה תרצו לשתף?</h3>
                                                <p className="text-sm text-ink-muted">בחרו את סוג הפוסט</p>
                                            </div>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                                                {[
                                                    { type: 'recommendations',     label: 'המלצה',  emoji: '⭐', tone: 'from-warning-50 to-surface-muted hover:from-warning-100/70' },
                                                    { type: 'poll',                label: 'סקר',    emoji: '📊', tone: 'from-info-50 to-surface-muted hover:from-info-100/70' },
                                                    { type: 'houses',              label: 'בתים',   emoji: '🏠', tone: 'from-brand-50 to-surface-muted hover:from-brand-100/70' },
                                                    { type: 'givetake',            label: 'קח-תן',  emoji: '🤝', tone: 'from-success-50 to-surface-muted hover:from-success-100/70' },
                                                    { type: 'communityInitiative', label: 'יוזמה',  emoji: '💡', tone: 'from-secondary-50 to-surface-muted hover:from-secondary-100/70' },
                                                    { type: 'other',               label: 'אחר',    emoji: '📝', tone: 'from-surface-muted to-surface hover:from-surface-sunken' },
                                                ].map((opt) => (
                                                    <motion.button
                                                        key={opt.type}
                                                        type="button"
                                                        whileHover={{ y: -2 }}
                                                        whileTap={{ scale: 0.97 }}
                                                        onClick={() => {
                                                            setCreatePostType(opt.type);
                                                            setShowPostOptions(false);
                                                            setShowCreatePostModal(true);
                                                        }}
                                                        className={`flex flex-col items-center justify-center gap-2 p-4 rounded-xl bg-gradient-to-br ${opt.tone} ring-1 ring-line-subtle hover:ring-brand-200 hover:shadow-soft transition-[box-shadow,ring-color,transform] duration-200 ease-smooth`}
                                                    >
                                                        <span className="text-2xl" aria-hidden="true">{opt.emoji}</span>
                                                        <span className="font-semibold text-ink text-sm">{opt.label}</span>
                                                    </motion.button>
                                                ))}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setShowPostOptions(false)}
                                                className="w-full mt-5 px-6 py-2.5 rounded-pill bg-surface-muted hover:bg-surface-sunken text-ink-muted font-semibold transition-colors"
                                            >
                                                ביטול
                                            </button>
                                        </motion.div>
                                    </div>
                                )}
                                {showCreatePostModal && (
                                    <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
                                        <div className="bg-white rounded-3xl p-8 w-[95vw] max-w-lg shadow-elevated relative animate-scale-in">
                                            <button
                                                onClick={() => setShowCreatePostModal(false)}
                                                className="absolute top-4 left-4 w-9 h-9 rounded-xl bg-surface-muted hover:bg-surface-sunken text-ink-muted font-bold flex items-center justify-center transition-all"
                                            >
                                                ✕
                                            </button>

                                            <CreateCommunityPost
                                                chatId={chatId}
                                                postType={createPostType}
                                                zoneId={zone}
                                                onSubmitSuccess={async () => {
                                                    setShowCreatePostModal(false);
                                                    await fetchPosts();
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                                {/* Filter chips — horizontally scrollable on mobile */}
                                <div className="relative -mx-2 px-2 overflow-x-auto scrollbar-hide">
                                    <div className="inline-flex flex-nowrap items-center gap-2 pb-1">
                                        {[
                                            { type: 'all', label: 'הכל' },
                                            { type: 'recommendations', label: 'המלצות' },
                                            { type: 'poll', label: 'סקרים' },
                                            { type: 'houses', label: 'בתים' },
                                            { type: 'givetake', label: 'קח-תן' },
                                            { type: 'communityInitiative', label: 'יוזמה' },
                                            { type: 'other', label: 'אחר' },
                                        ].map((filter) => {
                                            const active = postTypeFilter === filter.type;
                                            return (
                                                <button
                                                    key={filter.type}
                                                    type="button"
                                                    onClick={() => setPostTypeFilter(filter.type)}
                                                    aria-pressed={active}
                                                    className={`whitespace-nowrap px-4 py-1.5 rounded-pill text-sm font-semibold transition-[background-color,color,box-shadow,transform] duration-200 ease-smooth focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 ${
                                                        active
                                                            ? 'bg-brand-500 text-ink-inverse shadow-sm'
                                                            : 'bg-surface text-ink-muted ring-1 ring-line-subtle hover:bg-surface-muted hover:text-brand-700'
                                                    }`}
                                                >
                                                    {filter.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                {/* Posts Grid */}
                                {loading ? (
                                    <div className="flex flex-col items-center justify-center py-20">
                                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[var(--color-brand-primary)] border-t-transparent mb-4" />
                                        <p className="text-ink-muted font-semibold">טוען פוסטים...</p>
                                    </div>
                                ) : Array.isArray(communityPosts) && communityPosts.length > 0 ? (
                                    communityPosts
                                        .filter((post) =>
                                            postTypeFilter === 'all' ? true : post?.data?.type === postTypeFilter
                                        )
                                        .sort((a, b) => parseInt(b.created) - parseInt(a.created)) // newest first
                                        .map((post, idx) => {
                                            const { type, title, description, image, pollOptions } = post.data || {};
                                            const createdAt = new Date(parseInt(post.created) * 1000).toLocaleString('he-IL', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                            });

                                            // Map post type to a tiny badge tone
                                            const POST_TYPE_BADGE = {
                                                recommendations:      { label: 'המלצה', tone: 'bg-warning-50 text-warning-700 ring-warning-100' },
                                                poll:                 { label: 'סקר',   tone: 'bg-info-50 text-info-700 ring-info-100' },
                                                houses:               { label: 'בית',   tone: 'bg-brand-50 text-brand-700 ring-brand-200' },
                                                givetake:             { label: 'קח-תן', tone: 'bg-success-50 text-success-700 ring-success-100' },
                                                communityInitiative:  { label: 'יוזמה', tone: 'bg-secondary-50 text-secondary-700 ring-secondary-200' },
                                                other:                { label: 'פוסט',  tone: 'bg-surface-muted text-ink-muted ring-line' },
                                            };
                                            const postBadge = POST_TYPE_BADGE[type] || POST_TYPE_BADGE.other;

                                            return (
                                                <motion.div
                                                    key={post.post_id}
                                                    id={`post-${post.post_id}`}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.35, delay: Math.min(idx * 0.04, 0.32), ease: [0.22, 1, 0.36, 1] }}
                                                    className="relative bg-surface ring-1 ring-line-subtle rounded-2xl shadow-card hover:shadow-elevated transition-shadow duration-300 ease-smooth p-6 sm:p-7 max-w-2xl mx-auto"
                                                >
                                                    {/* Post Header */}
                                                    <div className="flex items-center justify-between gap-3 mb-4">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className={`inline-flex items-center text-2xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-pill ring-1 ${postBadge.tone}`}>
                                                                {postBadge.label}
                                                            </span>
                                                            <span className="text-xs text-ink-subtle font-medium">{createdAt}</span>
                                                        </div>
                                                        {post.owner && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDeletePost(post.post_id)}
                                                                className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-pill bg-danger-50 text-danger-700 ring-1 ring-danger-100 hover:bg-danger-100 transition-colors"
                                                            >
                                                                מחק
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* Post Content */}
                                                    {type === 'poll' ? (
                                                        <div className="space-y-4">
                                                            <h3 className="font-heading font-bold text-xl text-ink-strong">{title}</h3>

                                                            {/* Calculate totalVotes once here */}
                                                            {(() => {
                                                                const votes = post.data.votes || {};
                                                                const totalVotes = Object.values(votes).reduce((sum, val) => sum + parseInt(val || '0'), 0);

                                                                return (
                                                                    <div className="space-y-3">
                                                                        {pollOptions?.map((opt, idx) => {
                                                                            const voteCount = parseInt(votes[opt] || '0');
                                                                            const percent = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;

                                                                            return (
                                                                                <div key={idx} className="bg-surface-muted border border-line p-4 rounded-2xl space-y-2.5 hover:shadow-card transition-all">
                                                                                    <div className="flex justify-between text-sm font-bold text-ink-strong">
                                                                                        <span>{opt}</span>
                                                                                        <span className="text-[var(--color-brand-primary)]">{voteCount} ({percent}%)</span>
                                                                                    </div>
                                                                                    <div className="w-full bg-surface-sunken h-2.5 rounded-full overflow-hidden">
                                                                                        <div
                                                                                            className="bg-[var(--color-brand-primary)] h-full transition-all duration-300"
                                                                                            style={{ width: `${percent}%` }}
                                                                                        />
                                                                                    </div>
                                                                                    <button
                                                                                        onClick={async () => {
                                                                                            try {
                                                                                                const res = await safeFetch('https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/vote-poll', chatId, {
                                                                                                    method: 'POST',
                                                                                                    headers: { 'Content-Type': 'application/json' },
                                                                                                    body: JSON.stringify({
                                                                                                        post_id: post.post_id,
                                                                                                        chat_id: chatId,
                                                                                                        zone_id: zone,
                                                                                                        option: opt,
                                                                                                    }),
                                                                                                });

                                                                                                if (res.ok) {
                                                                                                    const updatedVotes = await res.json();
                                                                                                    // Update the votes object directly inside post.data.votes
                                                                                                    post.data.votes = updatedVotes;
                                                                                                    setCommunityPosts((prev) =>
                                                                                                        prev.map((p) =>
                                                                                                            p.post_id === post.post_id
                                                                                                                ? { ...p, data: { ...p.data, votes: updatedVotes } }
                                                                                                                : p
                                                                                                        )
                                                                                                    );
                                                                                                    // alert('✅ הצבעתך נקלטה!');
                                                                                                } else {
                                                                                                    const responseData = await res.json()
                                                                                                    if (responseData.error === 'user already voted')
                                                                                                        alert('❌ ניתן להצביע רק פעם אחת');
                                                                                                    else
                                                                                                        throw new Error('Server error');
                                                                                                }
                                                                                            } catch (err) {
                                                                                                console.error(err);
                                                                                                alert('❌ שגיאה בשליחה');
                                                                                            }
                                                                                        }}
                                                                                        className="w-full text-sm font-bold text-white bg-[var(--color-brand-primary)] hover:bg-[var(--color-brand-secondary)] rounded-xl py-2.5 transition-all shadow-card hover:shadow-card active:scale-95"
                                                                                    >
                                                                                        הצבע
                                                                                    </button>
                                                                                </div>
                                                                            );
                                                                        })}

                                                                        <div className="text-xs text-ink-muted mt-2 text-right font-semibold bg-surface-muted px-3 py-1.5 rounded-xl inline-block">
                                                                            סה"כ הצבעות: {totalVotes}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>
                                                    ) : (
                                                        <div className="space-y-4">
                                                            {/* Generic post: recommendations, give-take, etc. */}
                                                            <h3 className="font-heading font-bold text-xl text-ink-strong">{title}</h3>
                                                            <p className="text-ink leading-relaxed text-base">{description}</p>
                                                            {image && (
                                                                <img
                                                                    src={image}
                                                                    alt="Community post"
                                                                    className="w-full h-64 object-cover rounded-2xl border border-line"
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                    {/* Interaction Buttons */}
                                                    <div className="flex gap-2 mt-6 pt-4 border-t border-line">
                                                        {/* Comment trigger */}
                                                        <button
                                                            disabled={!chatId || chatId === 'null' || chatId === 'guest'}
                                                            onClick={() => handleToggleCommentPanel(post.post_id)}
                                                            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${(!chatId || chatId === 'null' || chatId === 'guest')
                                                                ? 'bg-surface-muted text-ink-subtle cursor-not-allowed'
                                                                : 'bg-surface-muted text-ink hover:bg-surface-muted border border-line hover:border-line-strong active:scale-95'
                                                                }`}
                                                            title="הוסף תגובה"
                                                        >
                                                            <span className="text-base">💬</span>
                                                            <span>תגובה</span>
                                                        </button>
                                                        {/* Emoji reaction trigger */}
                                                        <button
                                                            disabled={!chatId || chatId === 'null' || chatId === 'guest'}
                                                            onClick={() => handleToggleEmojiPanel(post.post_id)}
                                                            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${(!chatId || chatId === 'null' || chatId === 'guest')
                                                                ? 'bg-surface-muted text-ink-subtle cursor-not-allowed'
                                                                : 'bg-surface-muted text-ink hover:bg-surface-muted border border-line hover:border-line-strong active:scale-95'
                                                                }`}
                                                            title="הגב עם אימוג'י"
                                                        >
                                                            <span className="text-base">😊</span>
                                                            <span>ריאקציה</span>
                                                        </button>
                                                    </div>
                                                    {/* Emoji Reactions Display */}
                                                    {post.emoji_counts && (
                                                        <div className="flex flex-wrap gap-2 mt-4">
                                                            {Object.entries(post.emoji_counts)
                                                                .filter(([, count]) => count > 0)
                                                                .map(([emoji, count]) => (
                                                                    <div
                                                                        key={emoji}
                                                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-muted border border-line rounded-full text-sm font-bold"
                                                                    >
                                                                        <span className="text-base">{emoji}</span>
                                                                        <span className="text-ink">{count}</span>
                                                                    </div>
                                                                ))}
                                                        </div>
                                                    )}
                                                    {/* View Comments Button */}
                                                    {post.comments_count > 0 && (
                                                        <button
                                                            onClick={() => fetchPostComments(post.post_id)}
                                                            className="text-sm text-[var(--color-brand-primary)] hover:text-[var(--color-brand-secondary)] font-bold mt-3 flex items-center gap-1.5 transition-colors"
                                                        >
                                                            <span>💬</span>
                                                            <span>צפה ב-{post.comments_count} תגובות</span>
                                                        </button>
                                                    )}
                                                    {/* Comments Display */}
                                                    {openCommentsPostId === post.post_id && (
                                                        <div className="mt-4 p-5 bg-surface-muted rounded-2xl border border-line space-y-3">
                                                            <h4 className="font-heading font-bold text-ink-strong text-base">תגובות</h4>
                                                            {(postComments[post.post_id] || []).length > 0 ? (
                                                                postComments[post.post_id].map((comment, idx) => (
                                                                    <div key={idx} className="bg-white p-4 rounded-xl border border-line">
                                                                        <div className="text-xs text-ink-subtle mb-2 flex justify-between items-center">
                                                                            <span className="font-bold text-ink">{comment.user || 'אנונימי'}</span>
                                                                            <span className="bg-surface-muted px-2.5 py-1 rounded-lg">
                                                                                {new Date(parseFloat(comment.timestamp) * 1000).toLocaleString('he-IL', {
                                                                                    day: '2-digit',
                                                                                    month: '2-digit',
                                                                                    hour: '2-digit',
                                                                                    minute: '2-digit',
                                                                                })}
                                                                            </span>
                                                                        </div>
                                                                        <div className="text-ink text-sm leading-relaxed">{comment.text}</div>
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div className="text-ink-subtle italic text-sm text-center py-4">אין תגובות עדיין</div>
                                                            )}
                                                            <button
                                                                onClick={() => setOpenCommentsPostId(null)}
                                                                className="text-sm text-[var(--color-brand-primary)] hover:text-[var(--color-brand-secondary)] font-bold transition-colors"
                                                            >
                                                                סגור תגובות
                                                            </button>
                                                        </div>
                                                    )}

                                                    {/* Emoji Panel */}
                                                    {openEmojiPanel === post.post_id && (
                                                        <div className="absolute bottom-20 left-4 z-30 bg-white border border-line rounded-2xl shadow-elevated p-4 grid grid-cols-6 gap-2 animate-scale-in">
                                                            {emojiSet.map((emoji) => (
                                                                <button
                                                                    key={emoji}
                                                                    onClick={async () => {
                                                                        try {
                                                                            const res = await safeFetch('https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/react-to-post', chatId, {
                                                                                method: 'POST',
                                                                                headers: { 'Content-Type': 'application/json' },
                                                                                body: JSON.stringify({
                                                                                    post_id: post.post_id,
                                                                                    chatId: chatId,
                                                                                    reaction_emoji: emoji,
                                                                                    zone_id: zone
                                                                                }),
                                                                            });

                                                                            if (res.ok) {
                                                                                const updatedReactions = await res.json();
                                                                                setCommunityPosts((prev) =>
                                                                                    prev.map((p) =>
                                                                                        p.post_id === post.post_id
                                                                                            ? {
                                                                                                ...p,
                                                                                                emoji_counts: updatedReactions.emoji_counts,
                                                                                            }
                                                                                            : p
                                                                                    )
                                                                                );
                                                                                setOpenEmojiPanel(null);
                                                                            }
                                                                        } catch (err) {
                                                                            console.error('❌ Reaction failed:', err);
                                                                            alert('שגיאה בשליחה');
                                                                        }
                                                                    }}
                                                                    className="w-11 h-11 text-2xl hover:scale-110 hover:bg-surface-muted rounded-xl transition-all flex items-center justify-center border border-transparent hover:border-line"
                                                                >
                                                                    {emoji}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Comment Panel */}
                                                    {openCommentPanel === post.post_id && (
                                                        <div className="absolute bottom-20 left-20 z-30 bg-white border border-line rounded-2xl shadow-elevated p-5 w-80 animate-scale-in">
                                                            <h4 className="font-heading font-bold text-ink-strong text-base mb-3">הוסף תגובה</h4>
                                                            <textarea
                                                                rows={3}
                                                                value={commentText}
                                                                onChange={(e) => setCommentText(e.target.value)}
                                                                placeholder="כתוב את התגובה שלך..."
                                                                className="w-full border border-line rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-primary)] focus:border-[var(--color-brand-primary)] bg-surface-muted"
                                                            />
                                                            <button
                                                                onClick={async () => {
                                                                    if (!commentText.trim()) return;
                                                                    try {
                                                                        const res = await safeFetch('https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/react-to-post', chatId, {
                                                                            method: 'POST',
                                                                            headers: { 'Content-Type': 'application/json' },
                                                                            body: JSON.stringify({
                                                                                post_id: post.post_id,
                                                                                chatId: chatId,
                                                                                reaction_text: commentText,
                                                                                zone_id: zone
                                                                            }),
                                                                        });

                                                                        if (res.ok) {
                                                                            setCommentText('');
                                                                            setOpenCommentPanel(null);
                                                                            await fetchPostComments(post.post_id);
                                                                            await fetchPosts();
                                                                        } else {
                                                                            alert('❌ שגיאה בשליחה');
                                                                        }
                                                                    } catch (err) {
                                                                        console.error('❌ Comment error:', err);
                                                                        alert('❌ שגיאה בשליחה');
                                                                    }
                                                                }}
                                                                className="w-full mt-3 bg-[var(--color-brand-primary)] hover:bg-[var(--color-brand-secondary)] text-white py-2.5 rounded-xl font-bold text-sm transition-all shadow-card hover:shadow-card active:scale-95"
                                                            >
                                                                שלח תגובה
                                                            </button>
                                                            <button
                                                                onClick={() => setOpenCommentPanel(null)}
                                                                className="w-full mt-2 text-ink-muted hover:text-ink-strong text-sm font-semibold transition-colors"
                                                            >
                                                                ביטול
                                                            </button>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <ModerationReportButton
                                                            entity_id={post.post_id}
                                                            entity_type="post"
                                                            chatId={chatId}
                                                            zone={zone}
                                                        />
                                                    </div>

                                                </motion.div>
                                            );
                                        })
                                ) : (
                                    <div className="flex flex-col items-center justify-center text-center py-16 px-6 surface-soft rounded-2xl max-w-2xl mx-auto">
                                        <div className="relative mb-5">
                                            <span aria-hidden="true" className="absolute inset-0 bg-brand-200/40 rounded-pill blur-2xl" />
                                            <span className="relative inline-flex items-center justify-center w-20 h-20 rounded-pill bg-surface ring-4 ring-brand-50">
                                                <MessageSquare className="w-9 h-9 text-brand-500" strokeWidth={1.75} />
                                            </span>
                                        </div>
                                        <h3 className="heading-sm text-ink mb-2">אין עדיין פוסטים קהילתיים</h3>
                                        <p className="text-sm text-ink-muted max-w-xs">היו הראשונים לשתף משהו עם הקהילה — שאלה, המלצה, יוזמה...</p>
                                    </div>
                                )}
                            </div>
                        )}
                        {activeTab === 'groups' && (
                            <div className="space-y-6">
                                {/* Section header */}
                                <div className="flex items-end justify-between gap-4 flex-wrap">
                                    <div>
                                        <p className="eyebrow text-brand-700 mb-1">שיחות</p>
                                        <h2 className="heading-lg text-ink">קבוצות ושיחות</h2>
                                        <p className="text-sm text-ink-muted mt-1 max-w-md">
                                            כל הצ׳אטים הקיימים בקהילה מפלטפורמות שונות, במקום אחד.
                                        </p>
                                    </div>
                                    {(chatId !== null && chatId !== 'guest') && (
                                        <motion.button
                                            onClick={() => setShowCreateGroupModal(true)}
                                            whileHover={{ y: -1 }}
                                            whileTap={{ scale: 0.97 }}
                                            className="inline-flex items-center gap-2 h-10 px-4 rounded-pill bg-brand-500 hover:bg-brand-600 text-ink-inverse font-semibold text-sm shadow-card hover:shadow-elevated transition-shadow"
                                        >
                                            <Plus size={16} strokeWidth={2.5} />
                                            <span>קבוצה חדשה</span>
                                        </motion.button>
                                    )}
                                </div>

                                {/* Create Group Modal */}
                                {showCreateGroupModal && (
                                    <CreateGroupModal
                                        chatId={chatId}
                                        zone={zone}
                                        allCategories={allCategories}
                                        setShowCreateGroupModal={setShowCreateGroupModal}
                                        setRefreshKey={setRefreshKey}
                                    />
                                )}

                                <FilteredRolodexView
                                    items={groups}
                                    chatId={chatId}
                                    categories={groupsCategories}
                                    type="group"
                                    fromView="zone-details"
                                    zone={zone}
                                    onDelete={handleGroupDeleted}
                                    viewMode={viewMode}
                                    onViewModeChange={setViewMode}
                                    showFilters={showFilters}
                                    onToggleFilters={() => setShowFilters(prev => !prev)}
                                    onFilterChange={setActiveFilterCount}
                                    favoritesLists={favoritesLists}
                                    onToggleFavorite={handleToggleEntityInList}
                                    onCreateFavoriteList={handleCreateEntityList}
                                />
                            </div>
                        )}

                        {/* Skills & Knowledge Section */}
                        {activeTab === 'skills' && (
                            <SkillsKnowledgeSection
                                skillsKnowledge={skillsKnowledge}
                                chatId={chatId}
                                zoneId={zone}
                                userName={localStorage.getItem('user_name') || 'משתמש'}
                                userPhoto={localStorage.getItem('user_photo') || localStorage.getItem('profile_picture') || localStorage.getItem('google_profile_photo') || null}
                                onAddClick={(type) => {
                                    setIsEditMode(false);
                                    setEditingSkillId(null);
                                    setNewSkillData({ type: type || 'skill', title: '', description: '', categories: [], phone: '', hasWhatsApp: false, email: '', icon: 'Lightbulb' });
                                    setCustomSkillCategory('');
                                    setShowAddSkillModal(true);
                                }}
                                onDeleteSkill={handleDeleteSkill}
                                onEditSkill={handleEditSkill}
                            />
                        )}
                    </div>

                    {/* Update Groups Section */}
                    {/* <div className="mt-8 mb-6 flex justify-center">
                        <button
                            onClick={() => setShowCategoryGroups(true)}
                            className="px-8 py-4 font-semibold transition-all duration-200"
                            style={{
                                background: 'var(--color-brand-primary)',
                                color: 'var(--color-text-inverse)',
                                borderRadius: 'var(--border-radius-md)',
                                boxShadow: 'var(--shadow-elevated)',
                                fontFamily: 'var(--font-family-heading)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = 'var(--shadow-hover)';
                                e.currentTarget.style.filter = 'brightness(1.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = 'var(--shadow-elevated)';
                                e.currentTarget.style.filter = 'brightness(1)';
                            }}
                        >
                            <span>קבוצות עדכונים</span>
                        </button>
                    </div> */}

                    {/* Category Groups Modal */}
                    {showCategoryGroups && (
                        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
                            <div className="bg-white rounded-xl p-6 w-96 max-h-[80vh] overflow-y-auto text-center shadow-xl border border-line space-y-4">
                                <h2 className="text-xl font-bold text-ink-strong mb-4">
                                    קבוצות עדכונים של {zone}
                                </h2>
                                <p className="text-ink-muted text-sm mb-4">השארו מעודכנים בכל מה שמעניין אתכם</p>
                                {Object.entries(groupedCategoryGroups).map(([category, links]) => (
                                    <div key={category} className="flex items-center justify-between bg-surface-muted border border-line rounded-lg px-4 py-3 text-right">
                                        <span className="truncate font-medium text-ink-strong">{category}</span>
                                        <div className="flex gap-3">
                                            {links.whatsapp && (
                                                <a href={links.whatsapp} target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition px-3 py-1 bg-success-500 text-white rounded-lg font-semibold">WhatsApp</a>
                                            )}
                                            {links.telegram && (
                                                <a
                                                    href={links.telegram}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="hover:scale-110 transition px-3 py-1 font-semibold"
                                                    style={{
                                                        background: 'var(--color-brand-primary)',
                                                        color: 'var(--color-text-inverse)',
                                                        borderRadius: 'var(--border-radius-md)',
                                                        fontFamily: 'var(--font-family-body)'
                                                    }}
                                                >Telegram</a>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                <button
                                    onClick={() => setShowCategoryGroups(false)}
                                    className="mt-6 w-full bg-surface-sunken hover:bg-line text-ink-strong py-2 rounded-lg font-semibold transition"
                                >
                                    סגור
                                </button>
                            </div>
                        </div>
                    )}
                            </section>
                        </div>
            </motion.div>

            {/* Share / Edit profile + member detail + members list modals */}
            <ShareProfileModal
                isOpen={showShareProfileModal}
                onClose={() => setShowShareProfileModal(false)}
                chatId={chatId}
                zone={zone}
                onShared={() => fetchCommunityMembers(true)}
            />

            <EditProfileModal
                isOpen={showEditProfileModal}
                onClose={() => setShowEditProfileModal(false)}
                chatId={chatId}
                zone={zone}
                initialMember={memberToEdit || currentUserMember || selectedMember}
                onSaved={() => fetchCommunityMembers(true)}
            />

            <MemberDetailModal
                isOpen={showMemberDetailModal && !!selectedMember}
                member={selectedMember}
                chatId={chatId}
                onClose={() => setShowMemberDetailModal(false)}
                onEditProfile={() => {
                    setMemberToEdit(selectedMember);
                    setShowMemberDetailModal(false);
                    setShowEditProfileModal(true);
                }}
            />

            <ZoneMembersModal
                isOpen={showZoneMembersModal}
                onClose={() => setShowZoneMembersModal(false)}
                chatId={chatId}
                zone={zone}
                members={communityMembers}
                loading={loading}
                isUserMember={isUserMember}
                currentUserMember={currentUserMember}
                refetchMembers={() => fetchCommunityMembers(true)}
                onShareProfile={() => {
                    setShowZoneMembersModal(false);
                    setShowShareProfileModal(true);
                }}
                onSelectMember={(fullMember) => {
                    setSelectedMember(fullMember);
                    setShowMemberDetailModal(true);
                }}
                onEditProfile={(member) => {
                    setMemberToEdit(member);
                    setShowZoneMembersModal(false);
                    setShowEditProfileModal(true);
                }}
            />

            {/* Zone Share Modal */}
            {/* Share Zone — unified ShareMenu primitive */}
            <ShareMenu
                isOpen={showShareZoneModal}
                onClose={() => setShowShareZoneModal(false)}
                url={getZoneShareUrl()}
                title={`בואו להצטרף לקהילה ${zoneName || zone} במקומית`}
                heading="שתפו את הקהילה"
                description={zoneName || zone}
            />


            {/* "What's Happening Today" popup removed for now. */}

            {/* Add Skill/Knowledge Modal */}
            <AddSkillModal
                show={showAddSkillModal}
                onClose={() => {
                    setShowAddSkillModal(false);
                    setIsEditMode(false);
                    setEditingSkillId(null);
                }}
                onSubmit={handleAddSkill}
                newSkillData={newSkillData}
                setNewSkillData={setNewSkillData}
                allCategories={allCategories}
                customSkillCategory={customSkillCategory}
                setCustomSkillCategory={setCustomSkillCategory}
                isEditMode={isEditMode}
            />

            {/* Zone Group Chat Modal */}
            {showZoneChatModal && (
                <ZoneGroupChat
                    zoneName={zoneName || zone}
                    zoneId={zone}
                    chatId={chatId}
                    userName={localStorage.getItem('user_name') || 'משתמש'}
                    userPhoto={localStorage.getItem('user_photo') || localStorage.getItem('profile_picture') || localStorage.getItem('google_profile_photo') || null}
                    onClose={() => setShowZoneChatModal(false)}
                />
            )}
        </div>
    );
};
